import {DateTime, Diagram, DiagramType, nominate} from "platformed-client/types";

// Governance & Monitoring Org Structure Diagram
// Shows the governance hierarchy: Board → Audit Committee → Management → Internal Audit / Compliance
export const governanceStructure: Diagram = {
  diagram_id: nominate("DiagramId", "hardcoded-gov-001"),
  name: "Governance Structure",
  description:
    "Governance and monitoring structure showing the reporting lines between the board of directors, audit committee, executive management, internal audit, and operational teams responsible for financial reporting oversight.",
  diagram_type: "ProcessFlow" as DiagramType,
  created_at: "2026-01-15T10:00:00Z" as DateTime,
  updated_at: "2026-01-15T10:00:00Z" as DateTime,
  owner: null,
  deleted: null,
  lanes_enabled: false,
  lanes: {},
  generation_state: {type: "NotStarted"},

  nodes: {
    board: {
      diagram_node_id: nominate("DiagramNodeId", "gov-board"),
      label: "Board of Directors",
      description:
        "Ultimate governance body responsible for oversight of financial reporting, risk management, and internal control. Meets quarterly to review financial results and risk reports.",
      icon: "isoflow.user",
      position: [16, 0],
    },
    auditCommittee: {
      diagram_node_id: nominate("DiagramNodeId", "gov-audit-committee"),
      label: "Audit Committee",
      description:
        "Independent committee of the board responsible for overseeing financial reporting, external audit, and internal control effectiveness. Chaired by an independent director with financial expertise.",
      icon: "isoflow.user",
      position: [10, 5],
    },
    riskCommittee: {
      diagram_node_id: nominate("DiagramNodeId", "gov-risk-committee"),
      label: "Risk Committee",
      description:
        "Board committee overseeing enterprise risk management framework, risk appetite, and emerging risks. Coordinates with audit committee on risk-related internal control matters.",
      icon: "isoflow.user",
      position: [22, 5],
    },
    ceo: {
      diagram_node_id: nominate("DiagramNodeId", "gov-ceo"),
      label: "CEO",
      description:
        "Chief Executive Officer accountable for the overall system of internal control and setting the tone at the top for the control environment.",
      icon: "isoflow.user",
      position: [16, 10],
    },
    cfo: {
      diagram_node_id: nominate("DiagramNodeId", "gov-cfo"),
      label: "CFO",
      description:
        "Chief Financial Officer responsible for financial reporting integrity, accounting policies, and management's assessment of internal controls over financial reporting.",
      icon: "isoflow.user",
      position: [10, 15],
    },
    cro: {
      diagram_node_id: nominate("DiagramNodeId", "gov-cro"),
      label: "CRO",
      description:
        "Chief Risk Officer responsible for enterprise risk management, risk reporting to the board, and coordinating risk assessments across business units.",
      icon: "isoflow.user",
      position: [22, 15],
    },
    generalCounsel: {
      diagram_node_id: nominate("DiagramNodeId", "gov-general-counsel"),
      label: "General Counsel",
      description:
        "Oversees legal and regulatory compliance, ethics program, and whistleblower hotline. Reports to the CEO with direct access to the audit committee.",
      icon: "isoflow.user",
      position: [28, 10],
    },
    internalAudit: {
      diagram_node_id: nominate("DiagramNodeId", "gov-internal-audit"),
      label: "Internal Audit",
      description:
        "Independent assurance function reporting functionally to the audit committee and administratively to the CFO. Executes risk-based audit plan covering ICFR, operations, and compliance.",
      icon: "isoflow.user",
      position: [4, 20],
    },
    controller: {
      diagram_node_id: nominate("DiagramNodeId", "gov-controller"),
      label: "Controller",
      description:
        "Responsible for day-to-day financial reporting, accounting close process, and maintaining the control environment over financial transactions.",
      icon: "isoflow.user",
      position: [10, 22],
    },
    compliance: {
      diagram_node_id: nominate("DiagramNodeId", "gov-compliance"),
      label: "Compliance",
      description:
        "Monitors regulatory requirements, performs compliance testing, and tracks remediation of identified deficiencies. Reports to the CRO with dotted-line to general counsel.",
      icon: "isoflow.user",
      position: [22, 22],
    },
    externalAudit: {
      diagram_node_id: nominate("DiagramNodeId", "gov-external-audit"),
      label: "External Auditor",
      description:
        "Independent external audit firm engaged by the audit committee. Performs annual audit of financial statements and assessment of ICFR effectiveness.",
      icon: "isoflow.user",
      position: [4, 8],
    },
    ethicsHotline: {
      diagram_node_id: nominate("DiagramNodeId", "gov-ethics-hotline"),
      label: "Ethics / Whistleblower Hotline",
      description:
        "Anonymous reporting channel for employees and third parties to report suspected fraud, misconduct, or control violations. Managed by an independent third party with reports to the audit committee.",
      icon: "fi.storage.015.book",
      position: [33, 15],
    },
  },

  connections: {
    boardToAudit: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-board-audit"),
      source_node_id: nominate("DiagramNodeId", "gov-board"),
      target_node_id: nominate("DiagramNodeId", "gov-audit-committee"),
      label: "Delegates financial oversight",
      color: "blue",
      anchors: [],
    },
    boardToRisk: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-board-risk"),
      source_node_id: nominate("DiagramNodeId", "gov-board"),
      target_node_id: nominate("DiagramNodeId", "gov-risk-committee"),
      label: "Delegates risk oversight",
      color: "blue",
      anchors: [],
    },
    boardToCeo: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-board-ceo"),
      source_node_id: nominate("DiagramNodeId", "gov-board"),
      target_node_id: nominate("DiagramNodeId", "gov-ceo"),
      label: "Appoints & oversees",
      color: "blue",
      anchors: [],
    },
    ceoToCfo: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-ceo-cfo"),
      source_node_id: nominate("DiagramNodeId", "gov-ceo"),
      target_node_id: nominate("DiagramNodeId", "gov-cfo"),
      label: "Financial reporting",
      color: "blue",
      anchors: [],
    },
    ceoToCro: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-ceo-cro"),
      source_node_id: nominate("DiagramNodeId", "gov-ceo"),
      target_node_id: nominate("DiagramNodeId", "gov-cro"),
      label: "Risk management",
      color: "blue",
      anchors: [],
    },
    ceoToGc: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-ceo-gc"),
      source_node_id: nominate("DiagramNodeId", "gov-ceo"),
      target_node_id: nominate("DiagramNodeId", "gov-general-counsel"),
      label: "Legal & compliance",
      color: "blue",
      anchors: [],
    },
    cfoToController: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-cfo-controller"),
      source_node_id: nominate("DiagramNodeId", "gov-cfo"),
      target_node_id: nominate("DiagramNodeId", "gov-controller"),
      label: "Accounting & close",
      color: "blue",
      anchors: [],
    },
    cfoToIa: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-cfo-ia"),
      source_node_id: nominate("DiagramNodeId", "gov-cfo"),
      target_node_id: nominate("DiagramNodeId", "gov-internal-audit"),
      label: "Administrative reporting",
      color: "gray",
      anchors: [],
    },
    auditToIa: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-audit-ia"),
      source_node_id: nominate("DiagramNodeId", "gov-audit-committee"),
      target_node_id: nominate("DiagramNodeId", "gov-internal-audit"),
      label: "Functional reporting",
      color: "green",
      anchors: [],
    },
    auditToExternal: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-audit-external"),
      source_node_id: nominate("DiagramNodeId", "gov-audit-committee"),
      target_node_id: nominate("DiagramNodeId", "gov-external-audit"),
      label: "Engages & oversees",
      color: "green",
      anchors: [],
    },
    croToCompliance: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-cro-compliance"),
      source_node_id: nominate("DiagramNodeId", "gov-cro"),
      target_node_id: nominate("DiagramNodeId", "gov-compliance"),
      label: "Compliance monitoring",
      color: "blue",
      anchors: [],
    },
    gcToHotline: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-gc-hotline"),
      source_node_id: nominate("DiagramNodeId", "gov-general-counsel"),
      target_node_id: nominate("DiagramNodeId", "gov-ethics-hotline"),
      label: "Manages reporting channel",
      color: "orange",
      anchors: [],
    },
    hotlineToAudit: {
      diagram_connection_id: nominate("DiagramConnectionId", "gov-conn-hotline-audit"),
      source_node_id: nominate("DiagramNodeId", "gov-ethics-hotline"),
      target_node_id: nominate("DiagramNodeId", "gov-audit-committee"),
      label: "Reports to",
      color: "orange",
      anchors: [{position: [33, 5]}],
    },
  },

  regions: {},
  engagement: {
    engagement_id: nominate("EngagementId", "gov-engagement-1"),
    name: "",
    third_party: {name: "", third_party_id: nominate("ThirdPartyId", "gov-third-party-1")},
  },
};
